import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package, CheckCircle, Clock, XCircle, ArrowLeft, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { Input } from '../components/ui/input';
import useSEO from '../hooks/useSEO';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderTrackingPage = () => {
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  useSEO({ title: 'Track Your Order', description: 'Look up your NestFlow order status using your order number.' });

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      setError('Please enter your order number');
      return;
    }

    setLoading(true);
    setError('');
    setOrder(null);

    try {
      const response = await axios.get(`${API_URL}/api/orders/${orderNumber.trim()}`);
      setOrder(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No order found with that number. Please check and try again.');
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-6 h-6 text-[#34C759]" strokeWidth={1.5} />;
      case 'initiated':
      case 'pending':
        return <Clock className="w-6 h-6 text-[#FFA500]" strokeWidth={1.5} />;
      default:
        return <XCircle className="w-6 h-6 text-[#FF3B30]" strokeWidth={1.5} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid': return 'Payment Confirmed';
      case 'initiated': return 'Payment Initiated';
      case 'pending': return 'Processing';
      default: return status?.charAt(0).toUpperCase() + status?.slice(1);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-[#34C759]/10 text-[#34C759]';
      case 'initiated':
      case 'pending': return 'bg-[#FFA500]/10 text-[#FFA500]';
      default: return 'bg-[#FF3B30]/10 text-[#FF3B30]';
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="order-tracking-page">
      <Header />

      <main className="pt-28 pb-20">
        <div className="max-w-2xl mx-auto px-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#888] hover:text-[#1A1A1C] mb-8 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            Back
          </button>

          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-[#F5F5F5] rounded-full flex items-center justify-center mx-auto mb-5">
              <Package className="w-8 h-8 text-[#1A1A1C]" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A1C] font-['Manrope'] tracking-tight mb-2">
              Track Your Order
            </h1>
            <p className="text-[#888]">
              Enter your order number to check the status
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-10">
            <div className="flex gap-3">
              <Input
                type="text"
                placeholder="e.g. NF-2026-A3X7K"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                className="flex-1 bg-[#F5F5F5] border-0 rounded-full px-6 py-6 h-auto text-base font-mono focus:ring-2 focus:ring-[#1A1A1C]/20"
                data-testid="order-number-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-[#1A1A1C] text-white px-8 rounded-full font-semibold flex items-center gap-2 hover:bg-[#333] transition-all disabled:opacity-50"
                data-testid="track-order-btn"
              >
                {loading ? (
                  'Searching...'
                ) : (
                  <>
                    <Search className="w-5 h-5" strokeWidth={2} />
                    Track
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Error */}
          {error && (
            <div className="bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-xl p-5 text-center mb-8" data-testid="order-error">
              <p className="text-[#FF3B30] font-medium">{error}</p>
            </div>
          )}

          {/* Order Result */}
          {order && (
            <div className="bg-[#FAFAFA] rounded-2xl p-8 animate-fade-in" data-testid="order-result">
              {/* Status Badge */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  {getStatusIcon(order.payment_status)}
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${getStatusColor(order.payment_status)}`}>
                    {getStatusLabel(order.payment_status)}
                  </span>
                </div>
              </div>

              {/* Order Details */}
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-[#E8E8E8]">
                  <span className="text-[#888] text-sm">Order Number</span>
                  <span className="font-semibold text-[#1A1A1C] font-mono" data-testid="result-order-number">{order.order_number}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#E8E8E8]">
                  <span className="text-[#888] text-sm">Amount</span>
                  <span className="font-semibold text-[#1A1A1C]">${order.amount.toFixed(2)} {order.currency.toUpperCase()}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#E8E8E8]">
                  <span className="text-[#888] text-sm">Date</span>
                  <span className="text-[#555]">{new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>

                {/* Cart Items */}
                {order.items && order.items.length > 0 && (
                  <div className="pt-4">
                    <p className="text-sm font-semibold text-[#1A1A1C] mb-3">Items Ordered</p>
                    <div className="space-y-2">
                      {order.items.map((item, i) => (
                        <div key={i} className="flex justify-between items-center bg-white rounded-lg px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-[#1A1A1C]">{item.name}</p>
                            <p className="text-xs text-[#888]">Qty: {item.quantity}</p>
                          </div>
                          <span className="text-sm font-semibold text-[#1A1A1C]">${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Help */}
              <div className="mt-8 pt-6 border-t border-[#E8E8E8] text-center">
                <p className="text-sm text-[#888]">
                  Need help?{' '}
                  <a href="mailto:nestflowstore@gmail.com" className="text-[#1A1A1C] font-semibold hover:underline">
                    nestflowstore@gmail.com
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default OrderTrackingPage;
